import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import {
  Tag, Plus, Percent, DollarSign, Gift, Clock, Calendar,
  RefreshCw, Edit2, Trash2, ToggleLeft, ToggleRight, Search,
  Users, TrendingUp, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config/api';

const PROMOTION_TYPES = [
  { value: "percentage", label: "Percentage Off", icon: Percent, description: "e.g., 10% off entire order" },
  { value: "fixed", label: "Fixed Amount", icon: DollarSign, description: "e.g., $5 off orders over $25" },
  { value: "bogo", label: "Buy One Get One", icon: Gift, description: "Buy one, get one free or discounted" },
  { value: "free_item", label: "Free Item", icon: Gift, description: "Free item with qualifying purchase" },
  { value: "happy_hour", label: "Happy Hour", icon: Clock, description: "Time-based discount" }
];

export default function DiscountsPage() {
  const context = useOutletContext() || {};
  const { token = localStorage.getItem('backoffice_token'), manager = null } = context;
  const [loading, setLoading] = useState(true);
  const [promotions, setPromotions] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Create/Edit Modal
  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    code: '',
    promotion_type: 'percentage',
    discount_value: '',
    min_order_amount: '0',
    start_date: '',
    end_date: '',
    max_uses: '',
    is_active: true
  });
  const [isSaving, setIsSaving] = useState(false);

  const getHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const fetchPromotions = useLatestCallback(async () => {
    setLoading(true);
    try {
      const params = manager?.restaurant_id ? `?restaurant_id=${manager.restaurant_id}` : '';
      const response = await axios.get(`${API}/discounts/list${params}`, getHeaders());
      setPromotions(response.data.promotions || []);
    } catch (error) {
      toast.error('Failed to load promotions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  });

  const fetchStats = useLatestCallback(async () => {
    try {
      const params = manager?.restaurant_id ? `?restaurant_id=${manager.restaurant_id}` : '';
      const response = await axios.get(`${API}/discounts/stats${params}`, getHeaders());
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  });

  useEffect(() => {
    if (token) {
      fetchPromotions();
      fetchStats();
    }
  }, [token, fetchPromotions, fetchStats]);

  const openCreateModal = () => {
    setEditingPromotion(null);
    const now = new Date();
    const nextWeek = new Date(now);
    nextWeek.setDate(nextWeek.getDate() + 7);
    
    setFormData({
      name: '',
      description: '',
      code: '',
      promotion_type: 'percentage',
      discount_value: '',
      min_order_amount: '0',
      start_date: now.toISOString().slice(0, 16),
      end_date: nextWeek.toISOString().slice(0, 16),
      max_uses: '',
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (promotion) => {
    setEditingPromotion(promotion);
    setFormData({
      name: promotion.name,
      description: promotion.description || '',
      code: promotion.code || '',
      promotion_type: promotion.promotion_type,
      discount_value: promotion.discount_value?.toString() || '',
      min_order_amount: promotion.min_order_amount?.toString() || '0',
      start_date: promotion.start_date?.slice(0, 16) || '',
      end_date: promotion.end_date?.slice(0, 16) || '',
      max_uses: promotion.max_uses?.toString() || '',
      is_active: promotion.is_active
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Please enter a promotion name');
      return;
    }
    if (!formData.discount_value && ['percentage', 'fixed'].includes(formData.promotion_type)) {
      toast.error('Please enter a discount value');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        discount_value: formData.discount_value ? parseFloat(formData.discount_value) : null,
        min_order_amount: parseFloat(formData.min_order_amount) || 0,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null
      };

      if (editingPromotion) {
        await axios.put(`${API}/discounts/${editingPromotion.id}`, payload, getHeaders());
        toast.success('Promotion updated successfully');
      } else {
        await axios.post(`${API}/discounts/create`, payload, getHeaders()); // the session's restaurant
        toast.success('Promotion created successfully');
      }
      
      setShowModal(false);
      fetchPromotions();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save promotion');
    } finally {
      setIsSaving(false);
    }
  };

  const togglePromotion = async (promotionId) => {
    try {
      await axios.post(`${API}/discounts/${promotionId}/toggle`, {}, getHeaders());
      toast.success('Promotion status updated');
      fetchPromotions();
    } catch (error) {
      toast.error('Failed to update promotion');
    }
  };

  const deletePromotion = async (promotionId) => {
    if (!confirm('Are you sure you want to delete this promotion?')) return;
    
    try {
      await axios.delete(`${API}/discounts/${promotionId}`, getHeaders());
      toast.success('Promotion deleted');
      fetchPromotions();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete promotion');
    }
  };

  const getTypeIcon = (type) => {
    const typeConfig = PROMOTION_TYPES.find(t => t.value === type);
    const Icon = typeConfig?.icon || Tag;
    return <Icon className="h-4 w-4" />;
  };

  const getStatusBadge = (promotion) => {
    if (!promotion.is_active) {
      return <Badge variant="outline" className="bg-gray-100">Inactive</Badge>;
    }
    if (promotion.is_expired) {
      return <Badge variant="outline" className="bg-red-100 text-red-700">Expired</Badge>;
    }
    const now = new Date();
    const start = new Date(promotion.start_date);
    if (start > now) {
      return <Badge className="bg-yellow-100 text-yellow-700">Scheduled</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700">Active</Badge>;
  };

  const formatDiscount = (promotion) => {
    if (promotion.promotion_type === 'percentage') {
      return `${promotion.discount_value}% off`;
    }
    if (promotion.promotion_type === 'fixed') {
      return `$${promotion.discount_value} off`;
    }
    if (promotion.promotion_type === 'bogo') {
      return 'Buy One Get One';
    }
    if (promotion.promotion_type === 'free_item') {
      return 'Free Item';
    }
    if (promotion.promotion_type === 'happy_hour') {
      return `${promotion.discount_value}% off`;
    }
    return '-';
  };

  // Filter promotions
  const filteredPromotions = promotions.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || p.promotion_type === filterType;
    return matchesSearch && matchesType;
  });

  if (loading && promotions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="discounts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Discounts & Promotions</h1>
          <p className="text-gray-500">Create and manage promotional offers</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { fetchPromotions(); fetchStats(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={openCreateModal}>
            <Plus className="h-4 w-4 mr-2" />
            New Promotion
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Tag className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Promotions</p>
                  <p className="text-2xl font-bold">{stats.total_promotions}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Active</p>
                  <p className="text-2xl font-bold">{stats.active_promotions}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <XCircle className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Expired</p>
                  <p className="text-2xl font-bold">{stats.expired_promotions}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Uses</p>
                  <p className="text-2xl font-bold">{stats.total_uses}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search promotions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {PROMOTION_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Promotions Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Promotions</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredPromotions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Tag className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>No promotions found. Create your first promotion to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Promotion</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Min. Order</TableHead>
                    <TableHead>Uses</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPromotions.map((promotion) => (
                    <TableRow key={promotion.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{promotion.name}</p>
                          {promotion.description && (
                            <p className="text-sm text-gray-500 truncate max-w-48">{promotion.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {promotion.code ? (
                          <Badge variant="outline" className="font-mono bg-blue-50 text-blue-700">
                            {promotion.code}
                          </Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getTypeIcon(promotion.promotion_type)}
                          <span className="capitalize text-sm">
                            {promotion.promotion_type.replace('_', ' ')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold text-green-600">
                        {formatDiscount(promotion)}
                      </TableCell>
                      <TableCell>
                        {promotion.min_order_amount > 0 ? `$${promotion.min_order_amount}` : '-'}
                      </TableCell>
                      <TableCell>
                        {promotion.current_uses || 0}
                        {promotion.max_uses && ` / ${promotion.max_uses}`}
                      </TableCell>
                      <TableCell>{getStatusBadge(promotion)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => togglePromotion(promotion.id)}
                            title={promotion.is_active ? "Deactivate" : "Activate"}
                          >
                            {promotion.is_active ? (
                              <ToggleRight className="h-4 w-4 text-green-600" />
                            ) : (
                              <ToggleLeft className="h-4 w-4 text-gray-400" />
                            )}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => openEditModal(promotion)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-red-600"
                            onClick={() => deletePromotion(promotion.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingPromotion ? 'Edit Promotion' : 'Create New Promotion'}
            </DialogTitle>
            <DialogDescription>
              {editingPromotion ? 'Update promotion details' : 'Set up a new promotional offer'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Name */}
            <div>
              <Label>Promotion Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Summer Sale 20% Off"
              />
            </div>

            {/* Description */}
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the promotion..."
                rows={2}
              />
            </div>

            {/* Promo Code */}
            <div>
              <Label>Promo Code (optional)</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                placeholder="e.g., SUMMER20"
                className="font-mono uppercase"
              />
              <p className="text-xs text-gray-500 mt-1">Customers can enter this code at checkout</p>
            </div>

            {/* Type */}
            <div>
              <Label>Promotion Type *</Label>
              <Select 
                value={formData.promotion_type} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, promotion_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROMOTION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="h-4 w-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Discount Value */}
            {['percentage', 'fixed', 'happy_hour'].includes(formData.promotion_type) && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>
                    {formData.promotion_type === 'percentage' ? 'Percentage Off *' : 'Amount Off *'}
                  </Label>
                  <div className="relative">
                    {formData.promotion_type === 'percentage' ? (
                      <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    ) : (
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    )}
                    <Input
                      type="number"
                      value={formData.discount_value}
                      onChange={(e) => setFormData(prev => ({ ...prev, discount_value: e.target.value }))}
                      className="pl-9"
                      placeholder={formData.promotion_type === 'percentage' ? '10' : '5.00'}
                      min="0"
                      max={formData.promotion_type === 'percentage' ? '100' : undefined}
                    />
                  </div>
                </div>
                <div>
                  <Label>Min. Order Amount</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="number"
                      value={formData.min_order_amount}
                      onChange={(e) => setFormData(prev => ({ ...prev, min_order_amount: e.target.value }))}
                      className="pl-9"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>

            {/* Max Uses */}
            <div>
              <Label>Maximum Uses (leave empty for unlimited)</Label>
              <Input
                type="number"
                value={formData.max_uses}
                onChange={(e) => setFormData(prev => ({ ...prev, max_uses: e.target.value }))}
                placeholder="Unlimited"
                min="1"
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : (editingPromotion ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
